import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { accelerationRank, accelerationTrace } from '../../domain/cars/accelerationTrace';
import useWideStage from '../../application/motion/useWideStage';
import { EM_DASH, formatSpecNumber } from '../../domain/cars/carProperties';
import {
    curvePath, frameFor, labelPositions, TICK_SIZE,
} from './accelChartGeometry';

/**
 * The acceleration trace, drawn (SPEC-004 PR-4.5).
 *
 * A speed-against-time graph in the shape the 8-bit car screens drew it: a boxed
 * plot, a dotted grid, tick numbers a reader can hold in their head, and one
 * rising line. That reference is the point — this band is nostalgia for a
 * specific screen, and drawing it as a modern analytics chart would lose the
 * only reason it belongs in this product.
 *
 * The honesty rules it is drawn under, which are most of the markup below:
 *
 * - The MEASURED points are marks, the interpolation between them is a line, and
 *   the two are drawn differently on purpose (`accelerationTrace.js` explains
 *   why the interpolation cannot overshoot them).
 * - EVERY MEASURED POINT CARRIES ITS OWN FIGURES. A reader should not have to
 *   estimate 161 off a gridline drawn at 150 when the test published the number;
 *   these figures are the content of the chart and the curve is the shape they
 *   make. It is a deliberate departure from the usual rule against a number on
 *   every mark — that rule is about charts with dozens of points, and these have
 *   two to four, all of them published, none of them interpolated.
 * - The TOP SPEED is not on this chart at all, and its absence is the point. No
 *   test in the catalogue measured how long a car took to reach its maximum, so
 *   a curve drawn as far as that figure would be inventing the one number the
 *   graph does not have. The axes stop where the measurements stop; the top
 *   speed is on the plate and the spec table, where it is a published fact
 *   rather than the end of a line somebody could read a time off.
 * - The SOURCE is printed. These are a magazine's figures and the plate above
 *   is the marque's, and where they disagree a reader is entitled to know it is
 *   two different cars on two different days rather than an error.
 *
 * THE WORD ACCELERATION IS SET INSIDE THE PLOT (SPEC-004 PR-4.8). The reference
 * screen prints it large and grey in the empty quarter under a curve that rises
 * and flattens, and it is the last detail that makes this picture that picture.
 * It carries no information — the section above is already headed "Acceleration"
 * — so it is `aria-hidden` with the rest of the SVG and drawn dim enough that
 * the trace over it always wins.
 *
 * THE CURVE DRAWS ITSELF ON, and it is revealed by a CLIP rather than by a dash
 * offset. That is the idiom `RaceChart` established and the reason it gives
 * holds here too: a dash advances along the PATH, which is longer where the
 * curve is steep, so the tip would run ahead of itself exactly where the car is
 * accelerating hardest. A clip advances in TIME across the x axis, which is the
 * axis the chart is about. The clip is named for the car so two charts on one
 * page could never reveal against each other's rectangle.
 *
 * NO HOVER LAYER, deliberately, against the usual rule for a line chart. This
 * card is the deck's swipe surface, and a chart that captured pointer events to
 * show a tooltip would take the gesture with it (SPEC-004 D-8). Every point is
 * already a mark, already labelled with its own figures and already in the table
 * below, so there is nothing left for a tooltip to reveal.
 *
 * The SVG is `aria-hidden` and the same figures are a real `<table>`, because a
 * curve is not readable by ear at any level of labelling.
 *
 * The RANK above the plot is the one thing here that is not about this car's own
 * test: it is where the car sits among all twenty-four on the published 0-100, and
 * `accelerationRank` explains why that basis rather than the traces. It is drawn
 * as the value band draws its verdict — a label and a badge, one row, top of the
 * figure — because the two bands a reader scrolls past should not each invent a
 * header.
 */

/**
 * The plural categories an ordinal can fall into, and the ones the catalogues
 * carry copy for.
 *
 * English needs four — 1st, 2nd, 3rd, 4th — and `Intl.PluralRules` is what knows
 * which is which; a language added later may use categories English never does,
 * so anything outside this list falls back to `other` rather than rendering a
 * raw key at somebody. Chosen at the call site because that is already how this
 * app picks between `garage.count` and `garage.countOne`.
 */
const ORDINAL_CATEGORIES = ['one', 'two', 'few', 'other'];

/**
 * The watermark's type size and how far its corner sits inside the plot, both in
 * viewBox units.
 *
 * Set as attributes rather than in the stylesheet for `TICK_SIZE`'s reason
 * (SPEC-004 D-29): the position is a function of the size — the word is anchored
 * at its own end, so where it starts depends on how big it is — and a copy of
 * that number in each file is `--duration-deal`'s problem with a subtler
 * symptom.
 *
 * Sixteen units is the largest the longest translation of this word can be and
 * still leave the left half of the plot clear on a 320-unit viewBox. The inset
 * keeps it off the axes it would otherwise sit on.
 */
const WATERMARK = { size: 16, inset: 10 };

export default function CarAccelerationChart({ car, cars = [] }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    const trace = accelerationTrace(car);
    const rank = accelerationRank(car, cars);

    // Nothing at all for the three cars with no test of their own variant. An
    // empty pair of axes would read as "this car did not accelerate", which is a
    // worse answer than the band simply not being there.
    if (!trace) {
        return null;
    }

    const seconds = (value) => formatSpecNumber(value, { languageCode, fractionDigits: 0 });
    const speed = (value) => formatSpecNumber(value, { languageCode });

    // The frame, from the module the share card reads too. Where a gridline
    // falls, where a tick number sits against it and where a caption goes are
    // decided in ONE place now — the band and the card had drifted into two
    // different pictures of the same test, and nothing was comparing them.
    // The wide frame is chosen here rather than in CSS because a `viewBox` is
    // an ATTRIBUTE, and no media query can swap one drawing box for another.
    // `useWideStage` reads the same 1024 the sheets step at, and subscribes, so
    // dragging a window across the breakpoint redraws instead of keeping a
    // phone-weight chart until the next navigation.
    const {
        axis, captions, grid, insets, plot, project, ticks, view,
    } = frameFor(trace, useWideStage());

    const ordinal = (position) => {
        const category = new Intl.PluralRules(languageCode, { type: 'ordinal' }).select(position);

        return t(`cars.ordinal.${ORDINAL_CATEGORIES.includes(category) ? category : 'other'}`, {
            n: formatSpecNumber(position, { languageCode, grouped: false }),
        });
    };

    return (
        <figure className="accel">
            {/* Absent rather than blank for a car the deck is not ranking —
                a shelf that does not contain this car cannot say where it
                places in one. */}
            {rank ? (
                <p className="accel__standing">
                    <span className="accel__standing-label">{t('cars.accel.rankLabel')}</span>
                    <span className={`accel__rank${rank.position === 1 ? ' accel__rank--quickest' : ''}`}>
                        {t('cars.accel.rank', {
                            ordinal: ordinal(rank.position),
                            total: formatSpecNumber(rank.total, { languageCode, grouped: false }),
                        })}
                    </span>
                </p>
            ) : null}

            <svg
                className="accel__plot"
                viewBox={`0 0 ${view.width} ${view.height}`}
                aria-hidden="true"
                focusable="false"
            >
                {/* The grid first, so every mark is drawn over it. */}
                <g className="accel__grid">
                    {grid.horizontal.map((line) => (
                        <line key={`speed-${line.value}`} x1={line.x1} x2={line.x2} y1={line.y} y2={line.y} />
                    ))}
                    {grid.vertical.map((line) => (
                        <line key={`time-${line.value}`} x1={line.x} x2={line.x} y1={line.y1} y2={line.y2} />
                    ))}
                </g>

                {/* The two axes a reader measures against, drawn solid where the
                    grid is dotted — the corner is the origin and it has to look
                    like one. */}
                <path className="accel__axis" d={axis} />

                <g className="accel__ticks" fontSize={TICK_SIZE}>
                    {ticks.y.map((tick) => (
                        <text key={`speed-label-${tick.value}`} x={tick.x} y={tick.y} textAnchor={tick.anchor}>
                            {speed(tick.value)}
                        </text>
                    ))}
                    {ticks.x.map((tick) => (
                        <text key={`time-label-${tick.value}`} x={tick.x} y={tick.y} textAnchor={tick.anchor}>
                            {seconds(tick.value)}
                        </text>
                    ))}
                </g>

                {/* The axis names, INSIDE the plot with the numbers they belong
                    to — the speed caption turned on its side up the gutter, the
                    time caption in the bottom-left corner no gridline reaches.

                    They were two HTML paragraphs above and below the figure,
                    which put them at the reader's own font size and survived a
                    text-only zoom that a 9-unit `<text>` does not. That was the
                    better answer for a chart on its own; it is the wrong one for
                    a chart that also has to be the share card, where a caption
                    laid flat has nowhere to go that is empty for every car. The
                    information is not lost by moving them: the accessible
                    artefact for this figure has always been the `<table>` below,
                    which names both axes as real `<th>` text. */}
                <g className="accel__caption" fontSize={TICK_SIZE} aria-hidden="true">
                    <text
                        x={captions.y.x}
                        y={captions.y.y}
                        textAnchor={captions.y.anchor}
                        transform={`rotate(${captions.y.rotate} ${captions.y.x} ${captions.y.y})`}
                    >
                        {t('cars.accel.speedAxis')}
                    </text>
                    <text x={captions.x.x} y={captions.x.y} textAnchor={captions.x.anchor}>
                        {t('cars.accel.timeAxis')}
                    </text>
                </g>

                {/* Before the trace, so the curve and its marks are always
                    drawn OVER the word rather than through it. */}
                <text
                    className="accel__watermark"
                    x={plot.x + plot.width - WATERMARK.inset}
                    y={plot.y + plot.height - WATERMARK.inset}
                    textAnchor="end"
                    fontSize={WATERMARK.size}
                >
                    {t('cars.section.acceleration')}
                </text>

                <defs>
                    {/* Named for the car, the way `RaceChart` names its clip for
                        the lane: two of these in one document sharing an id
                        would have the second curve revealing against the first
                        one's rectangle. */}
                    <clipPath id={`accel-draw-${car.id}`}>
                        {/* KEYED TO THE CAR so the wipe replays. The chart is
                            mounted once now and updated in place — its axes,
                            grid and captions never move between cars — so the
                            one thing that has to be rebuilt is the rectangle
                            whose animation draws the new trace on. */}
                        <rect
                            className="accel__wipe"
                            key={car.id}
                            width={view.width}
                            height={view.height}
                        />
                    </clipPath>
                </defs>

                <path
                    className="accel__curve"
                    d={curvePath(trace.curve, project)}
                    clipPath={`url(#accel-draw-${car.id})`}
                />

                {/* The measured points, last and largest. These are the facts;
                    everything else on this chart is scaffolding around them.

                    Each one lands as the wipe reaches it, so the marks arrive in
                    the order the test recorded them. The index is the beat and
                    the stylesheet owns the interval — `--duel-side`'s shape,
                    applied to a chart. */}
                {trace.measured.map((point, position) => (
                    <circle
                        /* A point that is also a STANDING DISTANCE gets a ring
                           rather than a label. Every other point on this plot is
                           a speed and the axis says so; a distance is the one
                           thing the geometry cannot show, and the reader who
                           wants to know WHICH distance has the table below. A
                           third line of type on five points would cost the plot
                           its legibility to save that reader one glance. */
                        className={point.mark ? 'accel__point accel__point--distance' : 'accel__point'}
                        key={`${point.seconds}-${point.kmh}`}
                        cx={project.x(point.seconds)}
                        cy={project.y(point.kmh)}
                        r={3.5}
                        style={{ '--accel-mark': position }}
                    />
                ))}

                {/* And the figures themselves, on the marks they belong to.
                    A reader should not have to estimate 161 off a gridline at
                    150 when the test published the number — these ARE the
                    content of the chart, and the plot is the shape they make. */}
                {labelPositions(trace.measured, project, insets).map(({ point, speedY, x, y }, position) => (
                    <g
                        className="accel__figure"
                        key={`figure-${point.seconds}`}
                        style={{ '--accel-mark': position }}
                    >
                        <text className="accel__figure-speed" x={x} y={speedY} textAnchor="end">
                            {speed(point.kmh)}
                        </text>
                        <text className="accel__figure-time" x={x} y={y} textAnchor="end">
                            {t('cars.accel.timeValue', { seconds: formatSpecNumber(point.seconds, { languageCode, fractionDigits: 1 }) })}
                        </text>
                    </g>
                ))}
            </svg>

            {/* The same figures, readable by ear and by anyone who would rather
                read a number than estimate one off a gridline. */}
            <table className="visually-hidden">
                <caption>{t(`cars.accel.tableCaption.${trace.basis}`, { source: trace.source })}</caption>
                <thead>
                    <tr>
                        <th scope="col">{t('cars.spec.topSpeed')}</th>
                        <th scope="col">{t('cars.accel.timeAxis')}</th>
                        <th scope="col">{t('cars.accel.distanceAxis')}</th>
                    </tr>
                </thead>
                <tbody>
                    {trace.measured.map((point) => (
                        <tr key={`row-${point.seconds}`}>
                            <td>{t('cars.accel.speedValue', { speed: speed(point.kmh) })}</td>
                            <td>{t('cars.accel.timeValue', { seconds: formatSpecNumber(point.seconds, { languageCode, fractionDigits: 1 }) })}</td>
                            {/* The distance the run had covered, where the test
                                published one. An em dash rather than an empty
                                cell: most points are speeds and nothing else,
                                and a blank would read as a figure that went
                                missing. */}
                            <td>{point.mark ? t(`cars.accel.mark.${point.mark}`) : EM_DASH}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Printed rather than tucked into a title attribute: it is the line
                that makes the numbers above it citable instead of merely
                plausible, and a title attribute does not exist on a phone. */}
            {/* The line that says where these numbers came from — and, since
                SPEC-016 PR-16.2, WHAT KIND of numbers they are. The key is
                composed from the basis rather than the copy being conditional,
                because "measured by" and "simulated by" are different sentences
                rather than one sentence with a word swapped: a simulation has no
                car and no day to appeal to. A trace with no basis is refused at
                the build, so there is no fourth branch to fall through to. */}
            <figcaption className="accel__source">
                {t(`cars.accel.measuredBy.${trace.basis}`, { source: trace.source })}
            </figcaption>
        </figure>
    );
}
